import { useMemo, useState } from 'react';
import { Accordion } from 'components/DesignSystems/Accordion/Accordion';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { usePortalUserItems } from './arcgis-integration/arcGisPortal';
import type { ScopedPortalItem } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';
import {
  PortalItemRow,
  SelectablePortalItemsList,
} from './SelectablePortalItemsList';

function groupItemsByType(items: ScopedPortalItem[]) {
  const groups: Record<string, ScopedPortalItem[]> = {};

  for (const item of items) {
    const key = item.type?.trim() || 'Unknown type';
    (groups[key] ??= []).push(item);
  }

  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function selectionStats(items: ScopedPortalItem[], selectedIds: Set<string>) {
  const selectableItems = items.filter((item) => !!item.id);
  const selectedCount = selectableItems.reduce(
    (count, item) => count + (selectedIds.has(item.id!) ? 1 : 0),
    0
  );
  const all =
    selectableItems.length > 0 && selectedCount === selectableItems.length;
  const some = selectedCount > 0 && !all;

  return { all, some };
}

export function AuthenticatedPortalItems({
  selectedItemIds,
  onToggleItem,
  onToggleAllItems,
}: {
  selectedItemIds: Set<string>;
  onToggleItem: (item: ScopedPortalItem, checked: boolean) => void;
  onToggleAllItems: (items: ScopedPortalItem[], checked: boolean) => void;
}) {
  const [search, setSearch] = useState('');
  const group = useAppSelector(selectGroup);
  const { data, isError } = usePortalUserItems({
    portalState: {
      esriItem: 'Layers',
      owner: 'Esri',
      sort: 'num-views',
      searchTerm: search.trim().toLowerCase(),
    },
    group,
  });

  const portalItems = useMemo(() => data?.items ?? [], [data?.items]);
  const hasSearch = search.trim().length > 0;

  if (isError) return <div className="text-danger">Failed to load items.</div>;

  return (
    <SelectablePortalItemsList
      items={portalItems}
      selectedItemIds={selectedItemIds}
      onToggleAllItems={onToggleAllItems}
      search={search}
      onSearchChange={setSearch}
      scrollableListStyle={{ marginLeft: '-20px' }}
    >
      {(filteredItems) =>
        groupItemsByType(filteredItems).map(([type, groupedTypeItems]) => {
          const stats = selectionStats(groupedTypeItems, selectedItemIds);

          return (
            <Accordion
              key={`${type}-${hasSearch ? 'open' : 'closed'}`}
              eventKey={type}
              defaultExpanded={hasSearch}
              header={
                <div className="d-flex align-items-center gap-2 mb-1">
                  <StylishNewCheckbox
                    checked={stats.all}
                    indeterminate={stats.some}
                    disabled={groupedTypeItems.length === 0}
                    title={`Select all ${type} items`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleAllItems(
                        groupedTypeItems,
                        event.currentTarget.checked
                      );
                    }}
                  />

                  <div className="fw-bold">
                    {type}{' '}
                    <span className="text-muted">
                      ({groupedTypeItems.length})
                    </span>
                  </div>
                </div>
              }
              classes={{
                body: 'py-2 ms-5',
                card: 'bg-transparent',
                header: 'ps-0 py-2',
              }}
            >
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="ps-0">Title</th>
                      <th className="ps-0">Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedTypeItems.map((item) => (
                      <tr key={item.id}>
                        <PortalItemRow
                          item={item}
                          selectedItemIds={selectedItemIds}
                          onToggleItem={onToggleItem}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Accordion>
          );
        })
      }
    </SelectablePortalItemsList>
  );
}
